import React from "react";
import Carousel from "react-multi-carousel";
import { Box, Flex, Text, Heading } from "@theme-ui/components";
import { useBreakpointIndex } from "@theme-ui/match-media";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { colors, typography } from "../../styles/theme";
import { DisplayIcon } from "../atoms";

const GetValue = ({ moduleSectionHeading, modules }) => {
  const responsive = {
    allScreens: {
      breakpoint: { max: 4000, min: 0 },
      items: 1,
    },
  };
  const bpIndex = useBreakpointIndex({ defaultIndex: 1 });

  const CustomDot = ({ index, onClick, active }) => {
    const imageArray = modules.map((item) => item.image);
    const title = modules.map((item) => item.title);
    return (
      <Box
        sx={{
          backgroundColor: active ? colors.primary100 : "transparent",
          ...styles.customDotsStyles,
          color: active ? colors.primaryMain : colors.black,
          border: `1px solid ${colors.neutral200}`,
        }}
        onClick={onClick}
      >
        <GatsbyImage
          image={getImage(imageArray[index])}
          style={{ objectFit: "contain" }}
          alt="carousel-image"
        />
        <Heading
          as="h3"
          sx={{
            ...typography.h3,
            p: { m: 0 },
            mt: "0.75rem",
            color: "inherit",
          }}
          dangerouslySetInnerHTML={{ __html: title[index] }}
        />
      </Box>
    );
  };

  const CustomDotTab = ({ index, onClick, active }) => {
    const title = modules.map((item) => item.title);
    const iconArray = ["Users", "Wallet", "CalendarClock"];
    return (
      <Flex
        sx={{
          backgroundColor: active ? colors.primary100 : "transparent",
          color: active ? colors.primaryMain : colors.neutral900,
          ...CDTab.CDTabContainerStyles,
        }}
        onClick={onClick}
      >
        <DisplayIcon iconName={iconArray[index]} theme="primary" />
        <Text
          sx={CDTab.CDTabTextStyles}
          dangerouslySetInnerHTML={{ __html: title[index] }}
        />
      </Flex>
    );
  };

  return (
    <Flex
      sx={{
        flexDirection: "column",
      }}
    >
      <Box sx={styles.gradientBoxStyles}></Box>
      <Flex sx={styles.outerContainerStyles}>
        <Heading
          as="h2"
          sx={styles.headingStyles}
          dangerouslySetInnerHTML={{ __html: moduleSectionHeading }}
        />
        <Text
          sx={{
            ...typography.textMd400,
            textAlign: "center",
            mb: "3rem",
          }}
        >
          A platform loved by everyone in your company - by employers and
          employees
        </Text>
        <Box
          sx={{
            ...styles.carouselWrapper,
            ...styles.customDotWrapper,
            ...styles.commonStyles,
          }}
        >
          <Carousel
            responsive={responsive}
            arrows={false}
            showDots
            customDot={
              bpIndex > 2 ? (
                <CustomDot />
              ) : bpIndex === 2 ? (
                <CustomDotTab />
              ) : null
            }
            renderDotsOutside={true}
            additionalTransfrom={0}
            infinite
            autoPlay={true}
            autoPlaySpeed={3500}
            keyBoardControl
            minimumTouchDrag={80}
            slidesToSlide={1}
            renderButtonGroupOutside={true}
            customTransition="opacity 5s ease-in"
            transitionDuration={1000}
            containerClass="carousel-container"
          >
            {modules.length > 0 &&
              modules.map(({ id, title, advantages }, i) => (
                <Box id={id} key={`getvalue-${i}`} sx={{ width: "100%" }}>
                  <Box sx={styles.videoContainerStyles}>
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="auto"
                      width="100%"
                      height="100%"
                    >
                      <source src={advantages[0]?.desc} type="video/mp4" />
                    </video>
                  </Box>
                  <Flex
                    sx={{
                      flexDirection: ["column", "column", "row", "row"],
                      gap: "0.75rem",
                      "@media (min-width:900px) and (max-width:1240px)": {
                        flexDirection: "column",
                        width: "100%",
                      },
                    }}
                  >
                    <Heading
                      as="h3"
                      sx={styles.titleStyles}
                      dangerouslySetInnerHTML={{ __html: title }}
                    />

                    <Text
                      sx={styles.descStyles}
                      dangerouslySetInnerHTML={{ __html: advantages[0]?.desc1 }}
                    />
                  </Flex>
                </Box>
              ))}
          </Carousel>
        </Box>
      </Flex>
    </Flex>
  );
};

const styles = {
  gradientBoxStyles: {
    height: ["4rem", "4rem", "7rem", "7rem"],
    width: "100%",
    background:
      "linear-gradient(180deg, #FCE6FF 10.5%, #FDF2FF 46.5%, #FFFFFF 100%)",
    px: "0rem !important",
  },
  outerContainerStyles: {
    flexDirection: "column",
    justifyContent: "center",
    mb: ["4rem", "4rem", "7rem", "7rem"],
    px: ["0.75rem", "0.75rem", "0.75rem", "1.375rem"],
  },
  headingStyles: {
    ...typography.h2,
    textAlign: "center",
    p: { m: 0 },
    mb: "1.5rem",
  },
  videoContainerStyles: {
    overflow: "hidden",
    backgroundColor: colors.primary100,
    borderRadius: "1.25rem",
    p: "8%",
    mb: "0.75rem",
    "> video": {
      borderRadius: "0.75rem",
    },
  },
  titleStyles: {
    ...typography.h2,
    color: colors.primary700,
    backgroundColor: colors.primary100,
    borderRadius: "1.25rem",
    wordBreak: "break-word",
    width: "35%",
    p: { m: 0 },
    px: ["1.4rem", "1.4rem", "1.4rem", "2.25rem"],
    py: ["1.4rem", "1.4rem", "1.4rem", "2.063rem"],
    "@media (min-width:600px)": {
      display: "flex",
      alignItems: "center",
    },
    "@media (max-width:599px)": {
      textAlign: "center",
      width: "100%",
    },
    "@media (min-width:900px) and (max-width:1240px)": {
      width: "100%",
    },
  },
  descStyles: {
    ...typography.textLg500,
    backgroundColor: colors.primary100,
    borderRadius: "1.25rem",
    width: "65%",
    p: { m: 0 },
    p: "1.5rem",
    textAlign: "center",
    "@media (min-width:600px)": {
      textAlign: "left",
    },
    "@media (max-width:599px)": {
      width: "100%",
    },
    "@media (min-width:900px) and (max-width:1240px)": {
      width: "100%",
    },
  },
  customDotsStyles: {
    ...typography.textMd500,
    gap: "1rem",
    alignItems: "center",
    width: "19.688rem",
    height: "16.438rem",
    borderRadius: "1.25rem",
    padding: "1.5rem 3rem 1.5rem 3rem",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: colors.primary100,
      color: colors.primaryMain,
    },
  },
  commonStyles: {
    ".carousel-container": {
      width: "100%",
      p: 0,
    },
  },
  carouselWrapper: {
    width: "100%",
    display: "flex",
    gap: "3rem",
    "@media(max-width: 899px)": {
      flexDirection: "column",
      gap: "1.5rem",
    },
    ".react-multi-carousel-dot-list": {
      position: "relative !important",
      order: "-1",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "0.75rem",
      "@media (min-width:600px) and (max-width: 899px)": {
        flexDirection: "row",
      },
      "@media(max-width: 599px)": {
        flexDirection: "row",
        order: 1,
      },
    },
    ".react-multi-carousel-dot": {
      button: {
        border: "none",
        backgroundColor: colors.neutral200,
        "&:hover": {
          backgroundColor: colors.primary100,
        },
        "&:focus": {
          outline: "none",
        },
      },
    },
    ".react-multi-carousel-dot--active": {
      button: {
        backgroundColor: colors.primaryMain,
      },
    },
  },
  customDotWrapper: {
    position: "relative",
  },
};

const CDTab = {
  CDTabContainerStyles: {
    borderRadius: "0.5rem",
    p: "0.75rem 1.5rem",
    "&:hover": {
      backgroundColor: colors.primary100,
      color: `${colors.primaryMain} !important`,
    },
    cursor: "pointer",
  },
  CDTabTextStyles: {
    ...typography.textMd500,
    ml: "1rem",
    p: { m: 0 },
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    color: "inherit",
  },
};

export default React.memo(GetValue);
